import { useState } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES } from "../data/products";
import { useLang } from "../i18n";

const LINKS = [
    { id: "shop", key: "nav.shop" },
    { id: "french-series", key: "nav.frenchSeries" },
    { id: "attar-story", key: "nav.attarStory" },
    { id: "story", key: "nav.maison" },
    { id: "arabian-series", key: "nav.arabian" },
    { id: "bakhoor-collection", key: "nav.bakhoor" },
];

const LANGS = [
    ["en", "EN"],
    ["ar", "ع"],
    ["fr", "FR"],
    ["bn", "বাং"],
];

export const Nav = ({ cartCount, onCartOpen, onNavigate }) => {
    const { lang, setLang, t } = useLang();
    const [mobileOpen, setMobileOpen] = useState(false);
    const go = (id) => {
        setMobileOpen(false);
        onNavigate(id);
    };
    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-ivory/80 border-b border-gold/20"
            data-testid="main-nav"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
                <button
                    data-testid="nav-logo-button"
                    onClick={() => onNavigate("top")}
                    className="flex items-center gap-3 group"
                >
                    <img src={IMAGES.logo} alt="Al Zahra logo" className="h-10 w-10 object-contain mix-blend-multiply" data-testid="nav-logo-image" />
                    <span className="font-serif text-xl sm:text-2xl tracking-[0.25em] text-charcoal group-hover:text-golddark transition-colors duration-300">
                        AL ZAHRA
                    </span>
                </button>
                <nav className="hidden md:flex items-center gap-5 lg:gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => onNavigate(l.id)}
                            className="text-xs tracking-[0.2em] uppercase text-smoke hover:text-golddark transition-colors duration-300"
                        >
                            {t(l.key)}
                        </button>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <div className="flex border border-gold/30" data-testid="language-switcher">
                        {LANGS.map(([code, label]) => (
                            <button
                                key={code}
                                data-testid={`lang-${code}`}
                                onClick={() => setLang(code)}
                                className={`px-2.5 sm:px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                                    lang === code ? "bg-charcoal text-goldlight" : "text-smoke hover:text-golddark"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <button
                        data-testid="cart-open-button"
                        onClick={onCartOpen}
                        className="relative flex items-center gap-2 border border-gold/40 px-4 py-2 text-xs tracking-[0.2em] uppercase text-charcoal hover:bg-charcoal hover:text-goldlight transition-colors duration-300"
                    >
                        <ShoppingBag size={15} strokeWidth={1.5} />
                        <span className="hidden sm:inline">{t("nav.cart")}</span>
                        {cartCount > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-2 -right-2 w-5 h-5 bg-gold text-charcoal text-[10px] flex items-center justify-center font-semibold"
                            >
                                {cartCount}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Menu"
                        className="md:hidden flex items-center justify-center w-10 h-10 text-charcoal hover:text-golddark transition-colors duration-300"
                    >
                        {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.nav
                        key="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden overflow-hidden border-t border-gold/20 bg-ivory/95 backdrop-blur-xl"
                        data-testid="mobile-menu"
                    >
                        <div className="flex flex-col px-6 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="text-left py-3.5 border-b border-gold/10 last:border-0 text-sm tracking-[0.2em] uppercase text-smoke hover:text-golddark transition-colors duration-300"
                                >
                                    {t(l.key)}
                                </button>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
